import {
  Box,
  Collapse,
  Group,
  LoadingOverlay,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  UnstyledButton,
} from "@mantine/core";
import {
  CaretDownIcon,
  CaretUpDownIcon,
  CaretUpIcon,
  DatabaseIcon,
  MinusIcon,
  PlusIcon,
} from "@phosphor-icons/react";
import { EmptyState, LoadError, NoResults, TableSkeleton } from "@dehub/theme";
import qs from "qs";
import React, { JSX, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import useSwr from "swr";
import { IFilter } from "../../interfaces/IFilter";
import listSticky from "../list/list.module.css";
import classes from "./DataTable.module.css";

export * from "./RowAction";

export type ColumnType<T> = {
  title: string | React.ReactElement;
  sorter?: boolean;
  dataIndex?: string;
  render: (record: T, index: number) => JSX.Element | string;
  renderTitle?: (data: any, record: any, index: number) => JSX.Element | string;
  align?: "left" | "right" | "center";
  width?: string | number;
  textCollapsed?: boolean;
  spanWidth?: number;
  /** Sticky frozen column: checkbox or row-actions (shared list.module.css). */
  sticky?: "check" | "actions";
  className?: string;
};

export type ITableRef = { reload: () => void };

type Props = {
  name: string;
  columns: ColumnType<any>[];
  filters?: { [key: string]: any };
  pagination?: boolean;
  dataSource?: any[];
  loadData?: (filter?: IFilter) => Promise<any>;
  collapse?: boolean;
  collapseChildren?: (row: any) => React.ReactNode;
  limit?: number;
  onResult?: (data: any) => void;
  minWidth?: number;
  stickyHeader?: boolean;
  striped?: boolean | "even" | "odd";
  /** Optional props merged onto each body `<tr>` (e.g. data-* markers). */
  getRowProps?: (row: any, index: number) => React.HTMLAttributes<HTMLTableRowElement>;
  /** Applied to the outer Stack — use for drawer-scoped card chrome. */
  className?: string;
  /** Extra class on the pagination bar Group (drawer card footer). */
  paginationClassName?: string;
  /** Show "1–10 / N" range on the left of the pagination bar. */
  showPaginationRange?: boolean;
  /** Hide the page-size Select (default false = show). */
  hideLimitSelect?: boolean;
  /** Drop Mantine table border when the parent card supplies the outline. */
  withTableBorder?: boolean;
  /** Хайлт/шүүлтүүр идэвхтэй үед мөр байхгүй бол NoResults (өгөөгүй бол EmptyState). */
  filtered?: boolean;
  onClearFilters?: () => void;
  /** EmptyState-ийн гарчиг, тайлбар. */
  emptyTitle?: React.ReactNode;
  emptyDescription?: React.ReactNode;
};

function cellStickyClass(column: ColumnType<any>): string | undefined {
  const parts: string[] = [];
  if (column.sticky === "check") parts.push(listSticky.stickyCheck);
  if (column.sticky === "actions") {
    parts.push(listSticky.stickyActions);
    parts.push(listSticky.stickyEdge);
  }
  if (column.className) parts.push(column.className);
  return parts.length > 0 ? parts.join(" ") : undefined;
}

export const DataTable = React.forwardRef<ITableRef, Props>(function DataTable(
  {
    name,
    columns,
    filters = {},
    pagination = true,
    dataSource = [],
    loadData,
    collapse = false,
    collapseChildren,
    limit = 10,
    onResult,
    minWidth = 500,
    stickyHeader = false,
    striped = "even",
    getRowProps,
    className,
    paginationClassName,
    showPaginationRange = false,
    hideLimitSelect = false,
    withTableBorder = true,
    filtered = false,
    onClearFilters,
    emptyTitle,
    emptyDescription,
  },
  ref,
) {
  const { t } = useTranslation();
  const [opened, setOpened] = React.useState<number[]>([]);
  const [offset, setOffset] = React.useState({ page: 1, limit });
  const [sorted, setSorting] = React.useState<string[]>([]);

  const { data, error, mutate, isLoading } = useSwr(
    `table.${name}.[${offset.page}, ${offset.limit}]?${qs.stringify(filters)}`,
    async () => (loadData ? await loadData({ offset, filter: filters } as IFilter) : undefined),
    { fallbackData: { count: dataSource.length, rows: dataSource } },
  );

  React.useEffect(() => {
    if (data && onResult) onResult(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const onSort = (index?: string) => {
    if (!index) return;
    setSorting((state) => [index, state[0] === index ? (state[1] === "asc" ? "desc" : "asc") : "asc"]);
  };

  useImperativeHandle(ref, () => ({ reload: () => mutate() }));

  const rows = data?.rows ?? [];
  const colSpan = collapse ? columns.length + 1 : columns.length;
  const totalCount = data?.count ?? 0;
  const rangeStart = totalCount === 0 ? 0 : (offset.page - 1) * offset.limit + 1;
  const rangeEnd = Math.min(offset.page * offset.limit, totalCount);

  // DeHUB UI стандарт: хүснэгтийн 4 төлөв (ачаалж байна, алдаа, өгөгдөлгүй, үр дүнгүй). Толгой зөвхөн мөртэй үед.
  const stateNode =
    rows.length > 0 ? null : isLoading ? (
      <TableSkeleton rows={5} columns={Math.max(1, colSpan)} />
    ) : error ? (
      <LoadError onRetry={() => mutate()} />
    ) : filtered ? (
      <NoResults onClear={onClearFilters} />
    ) : (
      <EmptyState title={emptyTitle} description={emptyDescription} />
    );

  return (
    <Stack gap={showPaginationRange ? 0 : "sm"} pos="relative" className={className}>
      <LoadingOverlay visible={isLoading && rows.length > 0} zIndex={2} />

      {stateNode ?? (
      <Table.ScrollContainer minWidth={minWidth}>
        <Table
          striped={striped}
          highlightOnHover
          withTableBorder={withTableBorder}
          stickyHeader={stickyHeader}
          withRowBorders={false}
          horizontalSpacing={0}
          verticalSpacing={0}
          tabularNums
          classNames={{ table: classes.table, thead: classes.thead, td: classes.td }}
        >
          <Table.Thead>
            <Table.Tr className={listSticky.scrollRow}>
              {collapse && <Table.Th w={32} />}
              {columns.map((column, i) => (
                <Th
                  key={i}
                  column={column}
                  sorted={sorted[0] === column.dataIndex ? (sorted[1] as "asc" | "desc") : undefined}
                  onSort={() => onSort(column.dataIndex)}>
                  {column.title}
                  {column.renderTitle?.(data, column, i)}
                </Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row: any, i: number) => {
                const isOpen = opened.includes(i);
                const rowIndex = i + offset.page * offset.limit - offset.limit;
                const extra = getRowProps?.(row, rowIndex) ?? {};
                const { className: rowClassName, ...restRow } = extra;
                return (
                  <React.Fragment key={`row-${row?.id ?? i}`}>
                    <Table.Tr
                      {...restRow}
                      className={[listSticky.scrollRow, rowClassName].filter(Boolean).join(" ") || undefined}
                    >
                      {collapse && (
                        <Table.Td>
                          <UnstyledButton
                            className={classes.collapseBtn}
                            onClick={() =>
                              setOpened(isOpen ? opened.filter((x) => x !== i) : [...opened, i])
                            }
                            aria-label={isOpen ? "Collapse" : "Expand"}>
                            {isOpen ? <MinusIcon size={14} /> : <PlusIcon size={14} />}
                          </UnstyledButton>
                        </Table.Td>
                      )}
                      {columns.map((column, j) => (
                        <Table.Td
                          key={`cell-${row?.id ?? i}-${j}`}
                          ta={column.align ?? "left"}
                          className={cellStickyClass(column)}
                        >
                          {column.render(row, rowIndex)}
                        </Table.Td>
                      ))}
                    </Table.Tr>
                    {collapse && isOpen && (
                      <Table.Tr className={classes.collapseRow}>
                        <Table.Td colSpan={colSpan} className={classes.collapseCell}>
                          <Collapse expanded={isOpen}>
                            <Box p="md" bg="gray.0">
                              {collapseChildren?.(row)}
                            </Box>
                          </Collapse>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </React.Fragment>
                );
              })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      )}

      {pagination && totalCount > 0 && Math.ceil(totalCount / offset.limit) > 0 && (
        <Group
          justify={showPaginationRange ? "space-between" : "flex-end"}
          gap="sm"
          wrap="nowrap"
          className={[showPaginationRange ? classes.paginationBar : undefined, paginationClassName]
            .filter(Boolean)
            .join(" ") || undefined}
        >
          {showPaginationRange ? (
            <Group gap={6} wrap="nowrap">
              <Text size="sm" c="dimmed" className={classes.paginationRange}>
                {rangeStart}-{rangeEnd} / {totalCount}
              </Text>
              {!hideLimitSelect ? (
                <Select
                  onChange={(v: string | null) =>
                    v && setOffset((s) => ({ ...s, limit: parseInt(v, 10), page: 1 }))
                  }
                  value={`${offset.limit}`}
                  w={64}
                  size="xs"
                  data={["5", "10", "20", "30", "50", "100", "200"]}
                  aria-label="Page size"
                />
              ) : null}
            </Group>
          ) : null}
          <Group gap="sm" wrap="nowrap" ml={showPaginationRange ? "auto" : undefined}>
            <Pagination
              total={Math.ceil(totalCount / offset.limit)}
              value={offset.page}
              onChange={(page: number) => setOffset((s) => ({ ...s, page }))}
              siblings={1}
              boundaries={1}
              withControls
              size="sm"
              hideWithOnePage
            />
            {!showPaginationRange && !hideLimitSelect ? (
              <Select
                onChange={(v: string | null) =>
                  v && setOffset((s) => ({ ...s, limit: parseInt(v, 10), page: 1 }))
                }
                value={`${offset.limit}`}
                w={90}
                size="xs"
                data={["5", "10", "20", "30", "50", "100", "200"]}
              />
            ) : null}
          </Group>
        </Group>
      )}
    </Stack>
  );
});

type ThProps = {
  children: React.ReactNode;
  column: ColumnType<any>;
  sorted?: "asc" | "desc";
  onSort: () => void;
};

function Th({ children, column, sorted, onSort }: ThProps) {
  const Icon = sorted === "asc" ? CaretUpIcon : sorted === "desc" ? CaretDownIcon : CaretUpDownIcon;
  const stickyCls = cellStickyClass(column);

  const label = (
    <Text
      span
      fw={600}
      lh={1.3}
      w={column.spanWidth}
      fz="sm"
      className={column.sticky === "actions" ? listSticky.stickyActionsHead : undefined}
    >
      {children}
    </Text>
  );

  if (!column.sorter) {
    return (
      <Table.Th
        w={column.width}
        data-wrap={column.textCollapsed || undefined}
        className={[classes.th, stickyCls].filter(Boolean).join(" ") || undefined}>
        {label}
      </Table.Th>
    );
  }

  return (
    <Table.Th
      w={column.width ?? "max-content"}
      data-wrap={column.textCollapsed || undefined}
      className={[classes.th, stickyCls].filter(Boolean).join(" ") || undefined}>
      <UnstyledButton onClick={onSort} className={classes.sortBtn} data-active={sorted ? true : undefined}>
        {label}
        <Icon size={12} weight={sorted ? "bold" : "regular"} />
      </UnstyledButton>
    </Table.Th>
  );
}
